import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { EnergySource } from "@/types/energy-source/energy-source.types";
import { InformationSource } from "@/types/information-source/information-source.types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { energySourceService } from "@/services/energy-source/energySourceService";
import { useData } from "@/hooks/useData";
import { EnergySourceWithId } from "@/hooks/energy-sources/useEnergySourceTable";

interface EnergySourceFormProps {
  isOpen: boolean;
  onClose: () => void;
  energySourceToEdit?: EnergySourceWithId | null;
  onSuccess: () => void;
}

export const EnergySourceForm = ({
  isOpen,
  onClose,
  energySourceToEdit,
  onSuccess,
}: EnergySourceFormProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [carbonFactor, setCarbonFactor] = useState<number | undefined>(
    undefined
  );
  const [hydricFactor, setHydricFactor] = useState<number | undefined>(
    undefined
  );
  const [carbonSourceId, setCarbonSourceId] = useState<string>("");
  const [hydricSourceId, setHydricSourceId] = useState<string>("");

  // Validation state
  const [nameError, setNameError] = useState("");
  const [carbonFactorError, setCarbonFactorError] = useState("");
  const [hydricFactorError, setHydricFactorError] = useState("");
  const [carbonSourceError, setCarbonSourceError] = useState("");
  const [hydricSourceError, setHydricSourceError] = useState("");

  // Fetch information sources for the dropdown
  const { data: infoSources, loading: loadingInfoSources } =
    useData<InformationSource>({
      collectionName: "setup_infoSources",
    });

  // Initialize form when editing
  useEffect(() => {
    if (energySourceToEdit) {
      setName(energySourceToEdit.name || "");
      setCarbonFactor(energySourceToEdit.carbon_factor?.value);
      setHydricFactor(energySourceToEdit.hydric_factor?.value);
      setCarbonSourceId(energySourceToEdit.carbon_factor?.sourceId || "");
      setHydricSourceId(energySourceToEdit.hydric_factor?.sourceId || "");
    } else {
      // Reset form for new creation
      setName("");
      setCarbonFactor(undefined);
      setHydricFactor(undefined);
      setCarbonSourceId("");
      setHydricSourceId("");
    }
    // Reset validation errors when opening/closing the form
    setNameError("");
    setCarbonFactorError("");
    setHydricFactorError("");
    setCarbonSourceError("");
    setHydricSourceError("");
  }, [energySourceToEdit, isOpen]);

  const validateForm = () => {
    let isValid = true;

    // Validate name
    if (!name.trim()) {
      setNameError(t("validation.required"));
      isValid = false;
    } else {
      setNameError("");
    }

    // Validate Carbon Factor - now required
    if (carbonFactor === undefined || carbonFactor === null) {
      setCarbonFactorError(t("validation.required"));
      isValid = false;
    } else if (isNaN(carbonFactor) || carbonFactor < 0) {
      setCarbonFactorError(t("energySources.validation.invalidValue"));
      isValid = false;
    } else {
      setCarbonFactorError("");
    }

    // Validate Carbon Source - required when carbon factor is provided
    if (carbonFactor !== undefined && !carbonSourceId) {
      setCarbonSourceError(t("energySources.validation.sourceRequired"));
      isValid = false;
    } else {
      setCarbonSourceError("");
    }

    // Validate Hydric Factor - now required
    if (hydricFactor === undefined || hydricFactor === null) {
      setHydricFactorError(t("validation.required"));
      isValid = false;
    } else if (isNaN(hydricFactor) || hydricFactor < 0) {
      setHydricFactorError(t("energySources.validation.invalidValue"));
      isValid = false;
    } else {
      setHydricFactorError("");
    }

    // Validate Hydric Source - required when hydric factor is provided
    if (hydricFactor !== undefined && !hydricSourceId) {
      setHydricSourceError(t("energySources.validation.sourceRequired"));
      isValid = false;
    } else {
      setHydricSourceError("");
    }

    return isValid;
  };

  const handleCarbonFactorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setCarbonFactor(undefined);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setCarbonFactor(numValue);
    }
  };

  const handleHydricFactorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setHydricFactor(undefined);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setHydricFactor(numValue);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const energySourceData: Partial<EnergySource> = {
        name: name.trim(),
      };

      // Add carbon factor - now required
      if (carbonFactor !== undefined && carbonSourceId) {
        energySourceData.carbon_factor = {
          value: carbonFactor,
          sourceId: carbonSourceId,
        };
      }

      // Add hydric factor - now required
      if (hydricFactor !== undefined && hydricSourceId) {
        energySourceData.hydric_factor = {
          value: hydricFactor,
          sourceId: hydricSourceId,
        };
      }

      if (energySourceToEdit) {
        // Update existing energy source
        await energySourceService.edit(
          energySourceToEdit.id,
          energySourceData,
          user?.uid
        );
        toast({
          title: t("success"),
          description: t("energySources.updated"),
        });
      } else {
        // Create new energy source
        await energySourceService.create(
          energySourceData as Omit<EnergySource, "createdAt" | "updatedAt">,
          user?.uid
        );
        toast({
          title: t("success"),
          description: t("energySources.created"),
        });
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving energy source:", error);
      toast({
        title: t("error"),
        description: energySourceToEdit
          ? t("energySources.updateError")
          : t("energySources.createError"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter out archived info sources
  const activeInfoSources =
    infoSources?.filter((source) => !source.isArchived) || [];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !isLoading && !open && onClose()}
    >
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {energySourceToEdit
              ? t("energySources.editTitle")
              : t("energySources.addTitle")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Energy Source Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                {t("energySources.name")}{" "}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("energySources.namePlaceholder")}
                className={nameError ? "border-red-500" : ""}
              />
              {nameError && <p className="text-sm text-red-500">{nameError}</p>}
            </div>

            {/* Carbon Factor */}
            <div className="space-y-4 border rounded-md p-4">
              <h3 className="font-medium">
                {t("energySources.emission_factor")}
              </h3>

              <div className="space-y-2">
                <Label htmlFor="carbonFactor">
                  {t("energySources.factor_value")}
                  <span className="text-red-500"> *</span>
                </Label>
                <Input
                  id="carbonFactor"
                  type="number"
                  step="any"
                  min="0"
                  value={carbonFactor === undefined ? "" : carbonFactor}
                  onChange={handleCarbonFactorChange}
                  placeholder={t("energySources.factorPlaceholder")}
                  className={carbonFactorError ? "border-red-500" : ""}
                  disabled={isLoading}
                />
                {carbonFactorError && (
                  <p className="text-sm text-red-500">{carbonFactorError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="carbonSource">
                  {t("energySources.source")}
                  <span className="text-red-500"> *</span>
                </Label>
                <Select
                  value={carbonSourceId}
                  onValueChange={setCarbonSourceId}
                  disabled={loadingInfoSources}
                >
                  <SelectTrigger
                    id="carbonSource"
                    className={carbonSourceError ? "border-red-500" : ""}
                  >
                    <SelectValue
                      placeholder={t("energySources.sourcePlaceholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {activeInfoSources.map((source) => (
                      <SelectItem key={source.id} value={source.id}>
                        {source.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {carbonSourceError && (
                  <p className="text-sm text-red-500">{carbonSourceError}</p>
                )}
              </div>
            </div>

            {/* Hydric Factor */}
            <div className="space-y-4 border rounded-md p-4">
              <h3 className="font-medium">
                {t("energySources.hydric_factor")}
              </h3>

              <div className="space-y-2">
                <Label htmlFor="hydricFactor">
                  {t("energySources.factor_value")}
                  <span className="text-red-500"> *</span>
                </Label>
                <Input
                  id="hydricFactor"
                  type="number"
                  step="any"
                  min="0"
                  value={hydricFactor === undefined ? "" : hydricFactor}
                  onChange={handleHydricFactorChange}
                  placeholder={t("energySources.factorPlaceholder")}
                  className={hydricFactorError ? "border-red-500" : ""}
                  disabled={isLoading}
                />
                {hydricFactorError && (
                  <p className="text-sm text-red-500">{hydricFactorError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="hydricSource">
                  {t("energySources.source")}
                  <span className="text-red-500"> *</span>
                </Label>
                <Select
                  value={hydricSourceId}
                  onValueChange={setHydricSourceId}
                  disabled={loadingInfoSources}
                >
                  <SelectTrigger
                    id="hydricSource"
                    className={hydricSourceError ? "border-red-500" : ""}
                  >
                    <SelectValue
                      placeholder={t("energySources.sourcePlaceholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {activeInfoSources.map((source) => (
                      <SelectItem key={source.id} value={source.id}>
                        {source.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {hydricSourceError && (
                  <p className="text-sm text-red-500">{hydricSourceError}</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-gray-300 hover:bg-gray-100"
              disabled={isLoading}
            >
              {t("energySources.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {isLoading
                ? t("energySources.saving")
                : energySourceToEdit
                ? t("energySources.update")
                : t("energySources.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
